/**
 * The drawer's single mutation engine - object-level (action line) + per-row
 * (status-aware kebab), plus one reason dialog shared by both:
 *
 *   - ``markAllFixed``  -> POST mark-fixed/by-object (stamps every open tag,
 *                          incl. the weight-0 umbrella; status unchanged).
 *   - ``cantFixAll``    -> reason dialog -> bulkTransition(blockable, block).
 *   - ``onRowAction``   -> per-row kebab: Mark fixed / Clear marked fixed
 *                          (annotate); Can't fix yet / Ignore (reason ->
 *                          transition); Remove block / Reopen (transition, no
 *                          reason). Unblock fires a "Sync now" toast when the
 *                          FP has a connectable system.
 *
 * Reuses ``useExceptionLifecycle`` (no new mutation paths) and the global
 * toast. Resolution stays sync-confirmed: nothing here sets a "resolved"
 * status - the next sync confirms and closes. ``onMutated`` lets the drawer
 * mark itself dirty (host refetch on close) and refresh the Activity feed.
 */
import { createElement, useCallback, useMemo, useState } from 'react'
import { Button } from '@mui/material'
import { useExceptionLifecycle } from '../../../../hooks/useExceptionLifecycle'
import useSyncTrigger from '../../../../hooks/useSyncTrigger'
import { useNotify } from '../../../shared/ToastProvider'
import { poolwerxColors } from '../../../../theme/colors'
import type { ReasonVariant } from '../dialogs/ReasonDialog'
import type {
  EntryOverride,
  IssuesChipEntry,
} from '../../../../config/exceptions/shared/issuesState'
import type { RowAction } from '../../../../config/exceptions/shared/transitions'
import type { ExceptionDrawerFranchiseData } from './types'

export type { RowAction }

/** Pending reason-dialog target. ``tagIds`` is 1 for a per-row override or N
 *  for the object-level "Can't fix yet". */
export interface ReasonTarget {
  variant: ReasonVariant
  tagIds: number[]
}

interface UseDrawerObjectActionsArgs {
  fpCode: string
  dataDomain: string
  objectId: string
  /** Drawer's focused exception code. Threaded into mark-fixed/by-object so the
   *  server prefix-matches the whole product family for the composite per-outlet
   *  stock codes ("I've fixed all N" stamps every affected outlet). */
  exceptionCode: string
  /** Open, non-umbrella, NOT-yet-blocked tag ids - the object-level "Can't fix
   *  yet" blocks exactly these. Already-blocked rows are excluded so a bulk
   *  block never overwrites an existing block reason. */
  blockableTagIds: number[]
  /** Every open tag id on the object - the set "I've fixed all N" stamps, so
   *  the optimistic overlay can mark them all fixed at once. */
  allOpenTagIds: number[]
  /** Connection flags for the post-unblock "Sync now" toast affordance. Omit
   *  on surfaces where the FP's data isn't in scope - the affordance hides. */
  franchiseData?: ExceptionDrawerFranchiseData | null
  onMutated: () => void
  /** Optimistic per-tag status change to apply the instant a mutation succeeds,
   *  so the Issues list reflects it without waiting for the on-close refetch. */
  onApplied: (tagIds: number[], change: EntryOverride) => void
}

export interface UseDrawerObjectActionsResult {
  pending: boolean
  reasonTarget: ReasonTarget | null
  markAllFixed: () => Promise<void>
  cantFixAll: () => void
  onRowAction: (entry: IssuesChipEntry, action: RowAction) => Promise<void>
  closeReason: () => void
  submitReason: (reason: string) => Promise<void>
}

export function useDrawerObjectActions({
  fpCode,
  dataDomain,
  objectId,
  exceptionCode,
  blockableTagIds,
  allOpenTagIds,
  franchiseData,
  onMutated,
  onApplied,
}: UseDrawerObjectActionsArgs): UseDrawerObjectActionsResult {
  const { transition, bulkTransition, annotate, markFixedByObject, pending } =
    useExceptionLifecycle()
  const { notify, showSuccess, showError } = useNotify()
  const { triggerSync } = useSyncTrigger({
    fpCode,
    franchiseData: franchiseData ?? null,
  })
  const hasSyncableSystem = !!(
    franchiseData?.has_complete_lightspeed_connection ||
    franchiseData?.has_complete_xero_connection
  )
  const [reasonTarget, setReasonTarget] = useState<ReasonTarget | null>(null)

  const fail = useCallback(
    (err: any, fallback: string) => {
      const detail =
        err?.response?.data?.detail || err?.message || fallback
      showError(String(detail))
    },
    [showError],
  )

  // Post-unblock confirmation. With a connectable system, offer a "Sync now"
  // action on the toast so the cleared record refreshes from source; otherwise
  // a plain ack (the page-header freshness alert is the backstop). createElement
  // (not JSX) keeps this hook a .ts file.
  const notifyUnblocked = useCallback(() => {
    if (!hasSyncableSystem) {
      showSuccess('Block removed. Confirms after the next sync.')
      return
    }
    notify({
      severity: 'success',
      message: 'Block removed. Sync to refresh this record.',
      duration: 10000,
      action: createElement(
        Button,
        {
          size: 'small',
          disableRipple: true,
          onClick: () => {
            void triggerSync()
          },
          sx: {
            textTransform: 'none',
            fontWeight: 600,
            fontSize: '0.875rem',
            color: poolwerxColors.toastAction,
            minWidth: 0,
            p: 0,
            backgroundColor: 'transparent',
            '&:hover': {
              backgroundColor: 'transparent',
              color: poolwerxColors.toastActionHover,
              textDecoration: 'underline',
            },
          },
        },
        'Sync now',
      ),
    })
  }, [hasSyncableSystem, notify, showSuccess, triggerSync])

  const markAllFixed = useCallback(async () => {
    try {
      await markFixedByObject({ fpCode, dataDomain, objectId, exceptionCode })
      // Count-free: mark-fixed/by-object also stamps the weight-0 umbrella, so
      // a count here could contradict the "I've fixed all N" button label.
      showSuccess('Marked fixed - waiting for sync confirmation.')
      onApplied(allOpenTagIds, {
        marked_fixed: true,
        fix_confirmation_state: 'awaiting_sync',
      })
      onMutated()
    } catch (err) {
      fail(err, 'Could not mark these fixed.')
    }
  }, [markFixedByObject, fpCode, dataDomain, objectId, exceptionCode, allOpenTagIds, showSuccess, onApplied, onMutated, fail])

  const cantFixAll = useCallback(() => {
    if (blockableTagIds.length === 0) return
    setReasonTarget({ variant: 'block', tagIds: blockableTagIds })
  }, [blockableTagIds])

  const onRowAction = useCallback(
    async (entry: IssuesChipEntry, action: RowAction) => {
      // A grouped row (same problem at N outlets) carries every member tag id;
      // its kebab acts on the whole group at once. A single-occurrence row keeps
      // its lone tag - the single-tag path below is byte-identical to before.
      const tagIds =
        entry.member_tag_ids && entry.member_tag_ids.length > 0
          ? entry.member_tag_ids
          : typeof entry.tag_id === 'number'
            ? [entry.tag_id]
            : []
      if (tagIds.length === 0) return
      const annotateAll = (a: 'mark-fixed' | 'unmark-fixed') =>
        tagIds.length === 1
          ? annotate(tagIds[0], a)
          : Promise.all(tagIds.map((id) => annotate(id, a)))
      const transitionAll = (a: 'unblock' | 'reopen') =>
        tagIds.length === 1
          ? transition(tagIds[0], a)
          : bulkTransition(tagIds, a)
      if (action === 'mark-fixed') {
        try {
          await annotateAll('mark-fixed')
          showSuccess('Marked fixed. Confirms after the next sync.')
          onApplied(tagIds, {
            marked_fixed: true,
            fix_confirmation_state: 'awaiting_sync',
          })
          onMutated()
        } catch (err) {
          fail(err, 'Could not mark this fixed.')
        }
        return
      }
      // Clear the fixed marker (orthogonal annotation) - the row returns to a
      // plain open state, no lifecycle change.
      if (action === 'unmark-fixed') {
        try {
          await annotateAll('unmark-fixed')
          showSuccess('Cleared the fixed marker.')
          onApplied(tagIds, {
            marked_fixed: false,
            fix_confirmation_state: undefined,
          })
          onMutated()
        } catch (err) {
          fail(err, 'Could not clear the fixed marker.')
        }
        return
      }
      // unblock / reopen are reversibility moves - no reason, fire immediately.
      if (action === 'unblock' || action === 'reopen') {
        try {
          await transitionAll(action)
          if (action === 'unblock') {
            onApplied(tagIds, { blocked: false })
            notifyUnblocked()
          } else {
            // Reopen: ignored/resolved -> back to needs_review (clear both).
            onApplied(tagIds, {
              kind: undefined,
              marked_fixed: false,
              fix_confirmation_state: undefined,
            })
            showSuccess('Reopened for review.')
          }
          onMutated()
        } catch (err) {
          fail(err, 'Could not update this exception.')
        }
        return
      }
      // block / ignore need a reason - open the shared dialog scoped to the
      // row's tag(s) (all member outlets for a grouped row).
      setReasonTarget({ variant: action, tagIds })
    },
    [annotate, transition, bulkTransition, showSuccess, notifyUnblocked, onApplied, onMutated, fail],
  )

  const closeReason = useCallback(() => setReasonTarget(null), [])

  const submitReason = useCallback(
    async (reason: string) => {
      if (!reasonTarget) return
      const { variant, tagIds } = reasonTarget
      const action: 'block' | 'ignore' = variant === 'block' ? 'block' : 'ignore'
      try {
        if (tagIds.length === 1) {
          await transition(tagIds[0], action, reason)
        } else {
          await bulkTransition(tagIds, action, reason)
        }
        showSuccess(action === 'block' ? "Marked can't fix yet." : 'Ignored.')
        onApplied(
          tagIds,
          action === 'block'
            ? { blocked: true, block_reason: reason }
            : { kind: 'ignored' },
        )
        onMutated()
        setReasonTarget(null)
      } catch (err) {
        // Keep the dialog open so the user can retry; surface the failure.
        fail(err, 'Could not update this exception.')
      }
    },
    [reasonTarget, transition, bulkTransition, showSuccess, onApplied, onMutated, fail],
  )

  return useMemo(
    () => ({
      pending,
      reasonTarget,
      markAllFixed,
      cantFixAll,
      onRowAction,
      closeReason,
      submitReason,
    }),
    [pending, reasonTarget, markAllFixed, cantFixAll, onRowAction, closeReason, submitReason],
  )
}
